/**
 * Web Platform Service — delegates to the engine connector store.
 */

import { createPlatformServiceFromStore, translate } from "@magic-md/editor-core";
import { createSettingsService } from "@magic-md/editor-core";
import { useEngineConnectorStore } from "./engine-connector";
import { createWebUserSettingsAdapter } from "./user-settings";

export const createWebPlatformService = () => {
    const settings = createSettingsService({
        getActive: () => useEngineConnectorStore.getState().engineService,
        subscribe: (listener) =>
            useEngineConnectorStore.subscribe((state) =>
                listener(state.engineService),
            ),
        userAdapter: createWebUserSettingsAdapter(),
        clientInfo: {
            name: "magic-md-web-settings",
            version: "0.1.0",
        },
    });

    const applyConfigEffectNow = async ({
        owner,
        key,
        effect,
        value,
    }: {
        owner: "editor" | "engine" | "gateway" | "plugin" | "sidecar";
        key: string;
        effect: "immediate" | "nextSession" | "restart";
        value?: unknown;
    }) => {
        const engine = useEngineConnectorStore.getState().engineService;

        if (owner !== "sidecar") {
            return {
                success: false,
                message: translate("editor", "platform.webRestartUnsupported"),
            };
        }

        const match = key.match(/^sidecars\.([^.]+)\./);
        const sidecarId = match?.[1];
        if (!sidecarId) {
            return { success: false, message: translate("editor", "platform.sidecarIdInvalid") };
        }

        const list = await engine.sidecarList();
        const info = list.sidecars.find((item) => item.id === sidecarId) ?? null;
        const command =
            info?.manifest?.command ??
            info?.command ??
            `magic-md-${sidecarId}-backend`;
        const enabled = key.endsWith(".enabled") ? value !== false : true;

        if (!enabled) {
            if (info?.alive) {
                await engine.sidecarShutdown(sidecarId);
            }
            return {
                success: true,
                message: translate("editor", "platform.sidecarStopped", { sidecarId }),
            };
        }

        if (info?.alive) {
            await engine.sidecarShutdown(sidecarId);
        }
        await engine.sidecarSpawn(sidecarId, command);
        return {
            success: true,
            message:
                effect === "nextSession"
                    ? translate("editor", "platform.sidecarRestarted", { sidecarId })
                    : translate("editor", "platform.sidecarStarted", { sidecarId }),
        };
    };

    return createPlatformServiceFromStore(useEngineConnectorStore, {
        settings,
        openExternal: async (url: string) => {
            window.open(url, "_blank", "noopener");
        },
        getAssetUrl: (filePath: string) => {
            const endpoint = useEngineConnectorStore
                .getState()
                .localEndpoint.replace(/\/$/, "");
            return `${endpoint}/api/files/browse/raw?path=${encodeURIComponent(filePath)}`;
        },
        canApplyConfigEffectNow: ({ owner }) => owner === "sidecar",
        applyConfigEffectNow,
    });
};
